import { onMounted, onUnmounted, toRef, type Ref } from "vue";
import { useInternalFormApi, type GenericValues, type InternalFormApiId } from "./form-context";
import type { Path, PathValue } from "./types";

export type FieldMeta<TValue> = {
	/**
	 * Represent if handleBlur was called to the field
	 */
	touched: boolean;

	/**
	 * Represent if the value was ever changed.
	 * The only way to set dirty back to false if to reset the form.
	 */
	dirty: boolean;
	validated: boolean;
	valid: boolean;
	initialValue?: Partial<TValue>;
	observers: number;
};

export function useFieldMeta<TValues extends GenericValues, TPath extends Path<TValues>>(
	id: InternalFormApiId<TValues>,
	path: TPath
): Ref<FieldMeta<PathValue<TValues, TPath>>> {
	const {
		state: { metas },
	} = useInternalFormApi(id);

	function makeDefaultMeta(): FieldMeta<PathValue<TValues, TPath>> {
		return {
			touched: false,
			dirty: false,
			validated: false,
			valid: true,
			observers: 0,
		};
	}

	if (!metas[path]) {
		metas[path] = makeDefaultMeta();
	}

	const fieldMetas = toRef(metas, path) as Ref<FieldMeta<any>>;

	onMounted(() => {
		fieldMetas.value.observers += 1;
	});
	onUnmounted(() => {
		fieldMetas.value.observers -= 1;
	});

	return fieldMetas;
}
